import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { SaveAndAddButtons, TranslationType } from "go-core/components/SaveAndAddButtons";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { PaymentMethodApi, PaymentMethodSettingApi } from "../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../services/Api/api";
import { mapRoles } from "../../../../../components/ResourcePermissionTable";
import { getDefaultPaymentMethodValues, getParsedPaymentMethodSettingsToDefaultValues } from "../utils";
import PaymentMethodBasicInfoForm from "./PaymentMethodBasicInfoForm";
import PaymentMethodOnlinePaymentsForm from "./PaymentMethodOnlinePaymentsForm";
import PaymentMethodRolesForm from "./PaymentMethodRolesForm";

interface Props {
	paymentMethod?: PaymentMethodApi;
	settings?: PaymentMethodSettingApi[];
}

const PaymentMethodForm = (props: Props): JSX.Element => {
	const form = useForm<PaymentMethodApi>({
		criteriaMode: "all",
		defaultValues: getDefaultPaymentMethodValues(props.paymentMethod, props.settings),
	});
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();
	const history = useHistory();
	const { handleSubmit, formState, setError, reset } = form;
	const [saveLoading, setSaveLoading] = useState(false);
	const [saveAndAddLoading, setSaveAndAddLoading] = useState(false);
	const saveAndAddMode = useRef<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const parseFormData = (data: PaymentMethodApi) => {
		const roles = mapRoles(data);
		const settings = (data.settings || []).map((setting) => {
			if (typeof setting.value === "boolean") {
				return {
					...setting,
					value: setting.value.toString(),
				};
			}
			return setting;
		});

		return {
			...data,
			settings,
			roles,
			id: props.paymentMethod?.id,
		};
	};

	const onSubmit = handleSubmit(async (data: PaymentMethodApi) => {
		if (!validateCustomErrors()) return;

		saveAndAddMode.current ? setSaveAndAddLoading(true) : setSaveLoading(true);
		try {
			const newData = parseFormData(data);
			if (newData.id) {
				const params = { include: "roles,custom_field_templates,settings" };
				const res = await api.organization().updatePaymentMethod(newData, params);
				if (saveAndAddMode.current) {
					history.push(`/${organization.id}/settings/payment_methods/new`);
				}
				reset({
					...newData,
					custom_field_templates: newData.custom_field_templates.map((customFieldTemplate) => ({
						...customFieldTemplate,
						label: customFieldTemplate.name,
						id: customFieldTemplate.slug,
					})),
					settings: getParsedPaymentMethodSettingsToDefaultValues(res),
				});
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			} else {
				if (newData.price_wasted === undefined) newData.price_wasted = false;
				if (newData.print_copy === undefined) newData.print_copy = false;
				await api.organization().createPaymentMethod(newData);
				if (saveAndAddMode.current) reset({ print_receipt: true });
				else history.push(`/${organization.id}/settings/payment_methods`);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			}
		} catch (e) {
			handleError.form(e, setError, addFlash);
		} finally {
			setSaveAndAddLoading(false);
			setSaveLoading(false);
		}
	});

	const onCreateNextPaymentMethod = async () => {
		saveAndAddMode.current = true;
		onSubmit();
	};

	return (
		<FormDirty
			formState={formState}
			key="payment-form"
			loading={saveLoading}
			onSubmit={onSubmit}
			buttonSubmitCustom={
				<SaveAndAddButtons
					saveLoading={saveLoading}
					saveAndAddLoading={saveAndAddLoading}
					onSaveAndAddClick={onCreateNextPaymentMethod}
					translationType={TranslationType.FEMALE}
				/>
			}
		>
			<PaymentMethodBasicInfoForm form={form} setErrors={setErrors} />
			<PaymentMethodOnlinePaymentsForm form={form} />
			<PaymentMethodRolesForm form={form} paymentMethod={props.paymentMethod} />
		</FormDirty>
	);
};

export default PaymentMethodForm;
